import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
} from '@material-ui/core';
import { ExpandMore, PermIdentityOutlined } from '@material-ui/icons';
import { isNumber } from '@roc/feature-utils';
import { formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { useQuoteTypeStore } from '../../hooks/useQuoteTypeStore';
import { BorrowerFormStore } from '../../stores/common/borrowerFormStore';
import {
  getBooleanDisplayValue,
  getExperienceOptions,
  getValueOrDefaultDash,
} from '../../utils/utils';
import { ItemDisplay } from '../fixFlip/itemDisplay';
import { DetailsTable, DetailsTableField } from './detailsTable';
import { RoundedBackgroundIcon } from './roundedBackgroundIcon';
import { ViewControls } from './viewControls';

interface BorrowerInformationAccordionProps {
  store: BorrowerFormStore;
  onDeleteClick: (borrowerKey: string) => void;
  removeButtonDisabled?: boolean;
  showActions?: boolean;
  onEdit?: () => void;
  expanded?: boolean;
  onExpand: (expanded: boolean) => void;
}

export const BorrowerInformationAccordion = observer(
  ({
    store,
    onDeleteClick,
    onEdit,
    showActions = true,
    removeButtonDisabled = true,
    expanded,
    onExpand,
  }: BorrowerInformationAccordionProps) => {
    const borrower = store.getFormValues();

    const emailAddress = getValueOrDefaultDash(borrower.emailAddress);
    const borrowerDetails: DetailsTableField[] = [
      { label: 'Email', value: emailAddress, colSpan: 2 },
    ];

    return (
      <Accordion
        expanded={expanded}
        onChange={(e, expanded) => onExpand(expanded)}
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ExpandMore fontSize="large" />}
          aria-controls={`borrower-information-accordion-${store.key}`}
        >
          <BorrowerInformationAccordionSummary borrowerFormStore={store} />
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginLeft: '58px' }}
          >
            <Grid item xs={6}></Grid>
            {showActions && (
              <Grid item xs={6}>
                <Grid
                  container
                  spacing={2}
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <ViewControls
                    onDeleteClick={() => onDeleteClick(store.key)}
                    onEditClick={() =>
                      onEdit ? onEdit() : store.setEditMode(true)
                    }
                    removeButtonDisabled={removeButtonDisabled}
                  />
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <DetailsTable fields={borrowerDetails} />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }
);

export const BorrowerInformationAccordionSummary = observer(
  ({ borrowerFormStore }) => {
    const { quoteTypeStore } = useQuoteTypeStore();
    const { borrowersStore, entityStore } = quoteTypeStore;
    const borrower = borrowerFormStore.getFormValues();
    let fullName = `${borrower.firstName} ${borrower.lastName}`.trim();
    if (fullName === '') {
      fullName = '-';
    }
    const ficoScore = borrower.ficoScore ? borrower.ficoScore : 0;
    const experience = borrower.experience;

    let experienceTier = getExperienceOptions(
      quoteTypeStore.loanSubtype
    ).findIndex(item => item.value === experience);

    if (experienceTier >= 0) {
      experienceTier++;
    } else {
      experienceTier = 0;
    }
    const personalGuarantor = getBooleanDisplayValue(
      borrower.personalGuarantor
    );
    const showOwnershipPct = entityStore.showOwnershipPercentage;
    const percentageOfOwnership = isNumber(borrower.percentageOfOwnership)
      ? formatPercentage(borrower.percentageOfOwnership)
      : '-';

    return (
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item xs={5}>
          <Grid
            container
            spacing={2}
            justifyContent="flex-start"
            alignItems="center"
            style={{ flexWrap: 'nowrap' }}
          >
            <Grid item alignItems="center" style={{ height: '100%' }}>
              <RoundedBackgroundIcon
                Icon={PermIdentityOutlined}
                iconColor="#6D6D6D"
              />
            </Grid>
            <Grid item style={{ maxWidth: '100%' }}>
              <ItemDisplay
                label="Borrower Name"
                value={fullName}
                alignItems="flex-start"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={7}>
          <Grid
            container
            spacing={3}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Grid item>
              <ItemDisplay label="Estimated FICO Score" value={ficoScore} />
            </Grid>
            <Grid item>
              <ItemDisplay
                label="Experience Tier"
                value={experienceTier.toString()}
              />
            </Grid>
            <Grid item>
              <ItemDisplay
                label="Personal Guarantor"
                value={personalGuarantor}
              />
            </Grid>
            {showOwnershipPct && (
              <Grid item>
                <ItemDisplay label="Owner %" value={percentageOfOwnership} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
